import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config/api.config';
import './EPCISRules.css';

const CHECK_GROUPS = [
    {
        group: 'SBDH Header',
        fields: [
            { key: 'check_sender_id',               label: 'Sender ID',               desc: 'Validates Sender/Identifier is present and is a valid GS1 GLN or SGLN URN' },
            { key: 'check_receiver_id',             label: 'Receiver ID',             desc: 'Validates Receiver/Identifier is present and is a valid GS1 GLN or SGLN URN' },
            { key: 'check_document_identification', label: 'Document Identification', desc: 'Validates Standard, TypeVersion, InstanceIdentifier, Type, and CreationDateAndTime' },
            { key: 'check_sbdh_version',            label: 'SBDH Header Version',     desc: 'Validates HeaderVersion is present and equals "1.0" per SBDH 1.3' },
            { key: 'check_business_scope',          label: 'Business Scope',          desc: 'Validates BusinessScope and Scope elements are present' },
            { key: 'check_manifest',                label: 'Manifest',                desc: 'Warns if Manifest element is missing (required by some partners)' },
        ],
    },
    {
        group: 'Location Master Data',
        fields: [
            { key: 'check_location_master_data', label: 'Location Master Data',   desc: 'Requires EPCISMasterData to include a Location vocabulary (vtype:Location) with SGLN entries for all parties' },
            { key: 'check_location_name',        label: 'Facility Name',          desc: 'Validates cbv:mda#name is present for each location — receiving systems need facility names for display' },
            { key: 'check_location_address',     label: 'Street Address',         desc: 'Validates streetAddressOne, city, state, and postalCode are present for each location' },
            { key: 'check_location_country',     label: 'Country Code',           desc: 'Validates countryCode is present and is a valid ISO 3166-1 alpha-2 code (e.g., US, DE, GB)' },
            { key: 'check_location_xref',        label: 'Location Cross-Reference', desc: 'Warns if any SGLN used in event readPoint, bizLocation, source, or destination is not declared in the master data' },
        ],
    },
    {
        group: 'Product Master Data',
        fields: [
            { key: 'check_product_master_data', label: 'Product Master Data',      desc: 'Requires EPCISMasterData to include an EPCClass vocabulary (vtype:EPCClass) with SGTIN pattern entries' },
            { key: 'check_product_ndc',         label: 'FDA NDC',                  desc: 'Validates additionalTradeItemIdentificationTypeCode="FDA_NDC_11" and a valid 11-digit NDC — required for DSCSA pharma compliance' },
            { key: 'check_product_name',        label: 'Product & Manufacturer Name', desc: 'Validates regulatedProductName and manufacturerOfTradeItemPartyName are present' },
            { key: 'check_product_dosage',      label: 'Dosage Form',              desc: 'Validates cbv:mda#dosageFormType is present (e.g., Tablet, Capsule, Vial)' },
            { key: 'check_product_strength',    label: 'Strength Description',     desc: 'Validates cbv:mda#strengthDescription is present (e.g., "20 mg")' },
            { key: 'check_product_packaging',   label: 'Net Content / Packaging',  desc: 'Validates cbv:mda#netContentDescription is present (e.g., "100 Count bottle")' },
            { key: 'check_product_xref',        label: 'Product Cross-Reference',  desc: 'Warns if any SGTIN company.item used in events lacks a matching EPCClass master data entry' },
        ],
    },
    {
        group: 'DSCSA Compliance',
        fields: [
            { key: 'check_dscsa_statement', label: 'DSCSA Transaction Statement', desc: 'Validates the GS1 US Healthcare dscsaTransactionStatement element with affirmTransactionStatement=true — required for US pharmaceutical exchanges' },
        ],
    },
];

// Flat list for the active rule banner chips and table dots
const CHECK_FIELDS = CHECK_GROUPS.flatMap(g => g.fields);

const DEFAULT_RULE = {
    scope: 'global',
    sender_id: '',
    receiver_id: '',
    on_error: 'warn',
    // SBDH
    check_sender_id: true,
    check_receiver_id: true,
    check_document_identification: true,
    check_sbdh_version: true,
    check_business_scope: false,
    check_manifest: false,
    // Location
    check_location_master_data: false,
    check_location_name: false,
    check_location_address: false,
    check_location_country: false,
    check_location_xref: false,
    // Product
    check_product_master_data: false,
    check_product_ndc: false,
    check_product_name: false,
    check_product_dosage: false,
    check_product_strength: false,
    check_product_packaging: false,
    check_product_xref: false,
    // DSCSA
    check_dscsa_statement: false,
    description: '',
};

const severityColor = (status) => ({
    passed:   '#4ade80',
    warnings: '#fbbf24',
    errors:   '#f87171',
    blocked:  '#ef4444',
}[status] || '#94a3b8');

const severityBg = (status) => ({
    passed:   'rgba(74,222,128,0.08)',
    warnings: 'rgba(251,191,36,0.08)',
    errors:   'rgba(248,113,113,0.08)',
    blocked:  'rgba(239,68,68,0.08)',
}[status] || 'rgba(148,163,184,0.08)');

export default function EPCISRules() {
    const [rules, setRules]           = useState([]);
    const [loading, setLoading]       = useState(true);
    const [saving, setSaving]         = useState(false);
    const [showForm, setShowForm]     = useState(false);
    const [editRule, setEditRule]     = useState(null);
    const [form, setForm]             = useState(DEFAULT_RULE);
    const [error, setError]           = useState(null);

    // Test panel
    const [testXml, setTestXml]       = useState('');
    const [testResult, setTestResult] = useState(null);
    const [testing, setTesting]       = useState(false);

    const fetchRules = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/epcis/validation-rules`);
            const data = await res.json();
            setRules(data.data || []);
        } catch (e) {
            setError('Failed to load rules');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchRules(); }, [fetchRules]);

    const openCreate = () => { setEditRule(null); setForm(DEFAULT_RULE); setShowForm(true); };
    const openEdit   = (rule) => {
        setEditRule(rule);
        setForm({ ...rule, sender_id: rule.sender_id || '', receiver_id: rule.receiver_id || '' });
        setShowForm(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const method = editRule ? 'PUT' : 'POST';
            const url = editRule
                ? `${API_BASE_URL}/epcis/validation-rules/${editRule.id}`
                : `${API_BASE_URL}/epcis/validation-rules`;
            const body = { ...form, sender_id: form.sender_id || null, receiver_id: form.receiver_id || null };
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message);
            setShowForm(false);
            fetchRules();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this rule?')) return;
        try {
            await fetch(`${API_BASE_URL}/epcis/validation-rules/${id}`, { method: 'DELETE' });
            fetchRules();
        } catch (e) {
            setError('Failed to delete rule');
        }
    };

    const handleToggleActive = async (rule) => {
        try {
            await fetch(`${API_BASE_URL}/epcis/validation-rules/${rule.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ is_active: !rule.is_active }),
            });
            fetchRules();
        } catch (e) {
            setError('Failed to update rule');
        }
    };

    const handleTest = async () => {
        if (!testXml.trim()) return;
        setTesting(true);
        setTestResult(null);
        try {
            const res = await fetch(`${API_BASE_URL}/epcis/validate-header`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ xml: testXml }),
            });
            const data = await res.json();
            setTestResult(data);
        } catch (e) {
            setTestResult({ success: false, message: e.message });
        } finally {
            setTesting(false);
        }
    };

    const activeGlobal = rules.find(r => r.scope === 'global' && r.is_active);
    const routeRules   = rules.filter(r => r.scope === 'route');

    return (
        <div className="er-root">
            <div className="er-header">
                <div>
                    <h1 className="er-title">EPCIS Header Validation Rules</h1>
                    <p className="er-subtitle">
                        Control how EPCIS files are validated against GS1 EPCIS 1.2 SBDH master data standards
                        before being transmitted to AS2 partners.
                    </p>
                </div>
                <button className="er-btn er-btn--primary" onClick={openCreate}>
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    New Rule
                </button>
            </div>

            {error && (
                <div className="er-alert er-alert--error">
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {error}
                    <button onClick={() => setError(null)} className="er-alert__close">✕</button>
                </div>
            )}

            {/* Active global rule summary */}
            {activeGlobal && (
                <div className="er-active-banner">
                    <div className="er-active-banner__left">
                        <span className="er-dot er-dot--green"></span>
                        <span className="er-active-banner__label">Active Global Rule:</span>
                        <span className="er-active-banner__value">
                            On error — <strong>{activeGlobal.on_error === 'block' ? 'Block transmission' : 'Warn & allow'}</strong>
                        </span>
                    </div>
                    <div className="er-active-banner__checks">
                        {CHECK_FIELDS.filter(f => activeGlobal[f.key]).map(f => (
                            <span key={f.key} className="er-tag">{f.label}</span>
                        ))}
                    </div>
                </div>
            )}

            {/* Rules table */}
            <div className="er-section">
                <div className="er-section-title">
                    Configured Rules
                    <span className="er-badge">{rules.length}</span>
                </div>
                {loading ? (
                    <div className="er-loading">Loading rules…</div>
                ) : rules.length === 0 ? (
                    <div className="er-empty">No rules configured. Create a global rule to get started.</div>
                ) : (
                    <div className="er-table-wrap">
                        <table className="er-table">
                            <thead>
                                <tr>
                                    <th>Scope</th>
                                    <th>Sender ID</th>
                                    <th>Receiver ID</th>
                                    <th>On Error</th>
                                    <th>Checks</th>
                                    <th>Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rules.map(rule => (
                                    <tr key={rule.id} className={!rule.is_active ? 'er-row--inactive' : ''}>
                                        <td>
                                            <span className={`er-scope-badge er-scope-badge--${rule.scope}`}>
                                                {rule.scope}
                                            </span>
                                        </td>
                                        <td className="er-id-cell">{rule.sender_id || <span className="er-dim">Any</span>}</td>
                                        <td className="er-id-cell">{rule.receiver_id || <span className="er-dim">Any</span>}</td>
                                        <td>
                                            <span className={`er-action-badge er-action-badge--${rule.on_error}`}>
                                                {rule.on_error === 'block' ? '🚫 Block' : '⚠️ Warn'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="er-checks-cell">
                                                {CHECK_GROUPS.map(g => {
                                                    const on = g.fields.filter(f => rule[f.key]).length;
                                                    if (on === 0) return null;
                                                    return (
                                                        <span key={g.group} className="er-check-group-pill" title={`${g.group}: ${g.fields.filter(f => rule[f.key]).map(f => f.label).join(', ')}`}>
                                                            {g.group.split(' ')[0]} <strong>{on}</strong>
                                                        </span>
                                                    );
                                                })}
                                                {CHECK_FIELDS.filter(f => rule[f.key]).length === 0 && <span className="er-dim">None</span>}
                                            </div>
                                        </td>
                                        <td>
                                            <button
                                                className={`er-toggle ${rule.is_active ? 'er-toggle--on' : 'er-toggle--off'}`}
                                                onClick={() => handleToggleActive(rule)}
                                                title={rule.is_active ? 'Deactivate' : 'Activate'}
                                            >
                                                <span className="er-toggle__thumb" />
                                            </button>
                                        </td>
                                        <td>
                                            <div className="er-row-actions">
                                                <button className="er-btn-icon" onClick={() => openEdit(rule)} title="Edit">
                                                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                                <button className="er-btn-icon er-btn-icon--danger" onClick={() => handleDelete(rule.id)} title="Delete">
                                                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Test panel */}
            <div className="er-section">
                <div className="er-section-title">Test Validation</div>
                <p className="er-section-desc">
                    Paste an EPCIS XML document to preview what the rules engine would do with it.
                </p>
                <textarea
                    className="er-xml-input"
                    placeholder="Paste EPCIS XML here…"
                    value={testXml}
                    onChange={e => setTestXml(e.target.value)}
                    rows={10}
                />
                <button
                    className="er-btn er-btn--primary"
                    onClick={handleTest}
                    disabled={!testXml.trim() || testing}
                >
                    {testing ? 'Validating…' : 'Run Header Validation'}
                </button>

                {testResult && (
                    <div className="er-test-result" style={{ borderColor: severityColor(testResult.validationStatus), background: severityBg(testResult.validationStatus) }}>
                        <div className="er-test-result__header">
                            <span className="er-test-result__status" style={{ color: severityColor(testResult.validationStatus) }}>
                                {testResult.validationStatus === 'passed'   && '✓ Passed'}
                                {testResult.validationStatus === 'warnings'  && '⚠ Passed with warnings'}
                                {testResult.validationStatus === 'errors'    && '✗ Errors (would warn & allow)'}
                                {testResult.validationStatus === 'blocked'   && '🚫 Blocked'}
                                {!testResult.validationStatus && (testResult.success === false) && '✗ Error'}
                            </span>
                            {testResult.rule && (
                                <span className="er-test-result__rule">
                                    Applied rule: <strong>{testResult.rule.scope}</strong>
                                    {testResult.rule.scope === 'route' && ` (${testResult.rule.sender_id} → ${testResult.rule.receiver_id})`}
                                    &nbsp;· on_error: <strong>{testResult.rule.on_error}</strong>
                                </span>
                            )}
                        </div>

                        {(testResult.headerInfo || testResult.locationInfo || testResult.productInfo) && (
                            <div className="er-header-info">
                                {testResult.headerInfo && <>
                                    <div className="er-hi-row er-hi-section"><span>SBDH</span></div>
                                    <div className="er-hi-row"><span>SBDH present</span><span>{testResult.headerInfo.hasSbdh ? '✓ Yes' : '✗ No'}</span></div>
                                    {testResult.headerInfo.sender && <div className="er-hi-row"><span>Sender</span><span>{testResult.headerInfo.sender}</span></div>}
                                    {testResult.headerInfo.receiver && <div className="er-hi-row"><span>Receiver</span><span>{testResult.headerInfo.receiver}</span></div>}
                                    {testResult.headerInfo.instanceIdentifier && <div className="er-hi-row"><span>Instance ID</span><span>{testResult.headerInfo.instanceIdentifier}</span></div>}
                                    {testResult.headerInfo.creationDateAndTime && <div className="er-hi-row"><span>Created</span><span>{testResult.headerInfo.creationDateAndTime}</span></div>}
                                </>}
                                {testResult.locationInfo?.locationCount > 0 && <>
                                    <div className="er-hi-row er-hi-section"><span>Location Master Data</span></div>
                                    <div className="er-hi-row"><span>Locations declared</span><span>{testResult.locationInfo.locationCount}</span></div>
                                    {testResult.locationInfo.locations?.slice(0, 3).map(l => (
                                        <div key={l.id} className="er-hi-row">
                                            <span>{l.attrs['urn:epcglobal:cbv:mda#name'] || l.id}</span>
                                            <span>{l.attrs['urn:epcglobal:cbv:mda#city'] && `${l.attrs['urn:epcglobal:cbv:mda#city']}, ${l.attrs['urn:epcglobal:cbv:mda#state'] || ''} ${l.attrs['urn:epcglobal:cbv:mda#countryCode'] || ''}`}</span>
                                        </div>
                                    ))}
                                    {testResult.locationInfo.locationCount > 3 && <div className="er-hi-row"><span style={{color:'#475569'}}>…and {testResult.locationInfo.locationCount - 3} more</span><span></span></div>}
                                </>}
                                {testResult.productInfo?.productCount > 0 && <>
                                    <div className="er-hi-row er-hi-section"><span>Product Master Data</span></div>
                                    <div className="er-hi-row"><span>Products declared</span><span>{testResult.productInfo.productCount}</span></div>
                                    {testResult.productInfo.products?.slice(0, 3).map(p => (
                                        <div key={p.id} className="er-hi-row">
                                            <span>{p.attrs['urn:epcglobal:cbv:mda#regulatedProductName'] || p.id}</span>
                                            <span>{p.attrs['urn:epcglobal:cbv:mda#additionalTradeItemIdentification'] && `NDC: ${p.attrs['urn:epcglobal:cbv:mda#additionalTradeItemIdentification']}`}</span>
                                        </div>
                                    ))}
                                    {testResult.productInfo.productCount > 3 && <div className="er-hi-row"><span style={{color:'#475569'}}>…and {testResult.productInfo.productCount - 3} more</span><span></span></div>}
                                </>}
                            </div>
                        )}

                        {testResult.errors?.length > 0 && (
                            <div className="er-issue-list er-issue-list--error">
                                <div className="er-issue-list__title">Errors ({testResult.errors.length})</div>
                                {testResult.errors.map((e, i) => (
                                    <div key={i} className="er-issue">
                                        <span className="er-issue__badge er-issue__badge--error">{e.type}</span>
                                        <span className="er-issue__field">{e.field}</span>
                                        <span className="er-issue__msg">{e.message}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {testResult.warnings?.length > 0 && (
                            <div className="er-issue-list er-issue-list--warn">
                                <div className="er-issue-list__title">Warnings ({testResult.warnings.length})</div>
                                {testResult.warnings.map((w, i) => (
                                    <div key={i} className="er-issue">
                                        <span className="er-issue__badge er-issue__badge--warn">{w.type}</span>
                                        <span className="er-issue__field">{w.field}</span>
                                        <span className="er-issue__msg">{w.message}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Create / Edit modal */}
            {showForm && (
                <div className="er-modal-overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
                    <div className="er-modal">
                        <div className="er-modal__header">
                            <h2>{editRule ? 'Edit Rule' : 'New Validation Rule'}</h2>
                            <button className="er-modal__close" onClick={() => setShowForm(false)}>✕</button>
                        </div>

                        <div className="er-modal__body">
                            {/* Scope */}
                            <div className="er-field">
                                <label>Scope</label>
                                <div className="er-radio-group">
                                    {['global', 'route'].map(s => (
                                        <label key={s} className={`er-radio ${form.scope === s ? 'er-radio--active' : ''}`}>
                                            <input type="radio" value={s} checked={form.scope === s} onChange={() => setForm(f => ({ ...f, scope: s }))} />
                                            {s === 'global' ? 'Global (applies to all routes)' : 'Route-specific'}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {form.scope === 'route' && (
                                <div className="er-field-row">
                                    <div className="er-field">
                                        <label>Sender ID (GLN / SGLN)</label>
                                        <input
                                            className="er-input"
                                            placeholder="e.g. 0345678901234"
                                            value={form.sender_id}
                                            onChange={e => setForm(f => ({ ...f, sender_id: e.target.value }))}
                                        />
                                    </div>
                                    <div className="er-field">
                                        <label>Receiver ID (GLN / SGLN)</label>
                                        <input
                                            className="er-input"
                                            placeholder="e.g. 0987654321098"
                                            value={form.receiver_id}
                                            onChange={e => setForm(f => ({ ...f, receiver_id: e.target.value }))}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* On error action */}
                            <div className="er-field">
                                <label>When validation errors are found</label>
                                <div className="er-radio-group">
                                    <label className={`er-radio er-radio--warn ${form.on_error === 'warn' ? 'er-radio--active' : ''}`}>
                                        <input type="radio" value="warn" checked={form.on_error === 'warn'} onChange={() => setForm(f => ({ ...f, on_error: 'warn' }))} />
                                        <div>
                                            <strong>⚠ Warn &amp; Allow</strong>
                                            <span>Surface errors in the transmission log but continue to AS2 partner</span>
                                        </div>
                                    </label>
                                    <label className={`er-radio er-radio--block ${form.on_error === 'block' ? 'er-radio--active' : ''}`}>
                                        <input type="radio" value="block" checked={form.on_error === 'block'} onChange={() => setForm(f => ({ ...f, on_error: 'block' }))} />
                                        <div>
                                            <strong>🚫 Block Transmission</strong>
                                            <span>Stop the file from being sent. Log it as BLOCKED for review.</span>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Checks — grouped */}
                            {CHECK_GROUPS.map(({ group, fields }) => (
                                <div key={group} className="er-field">
                                    <label>{group}</label>
                                    <div className="er-checks-grid">
                                        {fields.map(({ key, label, desc }) => (
                                            <label key={key} className={`er-check-card ${form[key] ? 'er-check-card--on' : ''}`}>
                                                <div className="er-check-card__top">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!form[key]}
                                                        onChange={e => setForm(f => ({ ...f, [key]: e.target.checked }))}
                                                    />
                                                    <span className="er-check-card__label">{label}</span>
                                                </div>
                                                <p className="er-check-card__desc">{desc}</p>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            {/* Description */}
                            <div className="er-field">
                                <label>Description <span className="er-opt">(optional)</span></label>
                                <input
                                    className="er-input"
                                    placeholder="e.g. Strict rule for Trading Partner A"
                                    value={form.description}
                                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                />
                            </div>

                            {error && <div className="er-alert er-alert--error">{error}</div>}
                        </div>

                        <div className="er-modal__footer">
                            <button className="er-btn er-btn--ghost" onClick={() => setShowForm(false)}>Cancel</button>
                            <button className="er-btn er-btn--primary" onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving…' : editRule ? 'Save Changes' : 'Create Rule'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
